import { Button, type ButtonProps } from '../Button';
import { Typography } from '../typography';

import { type ToastVariant, toastVariants } from './toastVariants';

import type { ComponentPropsWithoutRef, MouseEvent, ReactNode } from 'react';
import type { ToastContentProps, TypeOptions } from 'react-toastify';

import s from './Toast.module.css';

type ActionClickArgs = {
  close: ToastContentProps['closeToast'];
  e: MouseEvent<HTMLButtonElement>;
};

type ToastActionProps = Omit<ButtonProps, 'children' | 'color' | 'onClick' | 'size'> & {
  children: ReactNode;
  onClick?: (args: ActionClickArgs) => void;
};

type Props = Omit<ComponentPropsWithoutRef<'div'>, 'children'> &
  Omit<ToastVariant, 'type' | 'withAction'> &
  Partial<ToastContentProps> & {
    children: ReactNode;
    actionProps?: ToastActionProps;
  };

const buttonColorVariants: Record<TypeOptions, ButtonProps['color']> = {
  default: 'primary',
  error: 'alternative',
  info: 'primary',
  success: 'primary',
  warning: 'alternative',
};

export const Toast = ({
  actionProps,
  children,
  className,
  closeToast,
  data: _data,
  // Exclude from DOM props
  isPaused: _isPaused,
  toastProps,
  ...props
}: Props) => {
  const type = toastProps!.type;

  const withAction = Boolean(actionProps);

  const handleActionClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!actionProps?.onClick) return;

    actionProps.onClick({ close: closeToast!, e });
  };

  return (
    <div className={toastVariants({ className, type, withAction })} {...props}>
      <Typography className={s.typography} component="p" variant="note">
        {children}
      </Typography>
      {withAction && (
        <Button {...actionProps} color={buttonColorVariants[type]} onClick={handleActionClick} size="small" />
      )}
    </div>
  );
};
